import React from 'react';
import { useTranslation } from 'react-i18next';
import { LouiseOffer, isOfferSuccessfull } from '../../../../../types/Quotes';
import SingleOfferMainButton from './SingleOfferMainButton';
import SingleOfferPrice from './SingleOfferPrice';
import SingleOfferDocuments from './SingleOfferDocuments';
import {
  Skeleton,
  VStack,
  Center,
  Flex,
  useDisclosure,
} from '@wegroup/design-system';
import DotLoaderWithText from '../../../../common/src/components/DotLoader';
import useIsNewRoute from '../../features/offers/hooks/useIsNewRoute';
import OfferActionLink from '../OfferActionLink';
import EmailCalculationModal from '../../features/offers/components/email-calculation/EmailCalculationModal';
import useCloseOnline from '../../features/offers/hooks/useCloseOnline';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

export const SummaryContainer: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => (
  <Flex direction="column" borderLeft="1px" borderColor="gray.50">
    {children}
  </Flex>
);

interface Props {
  offer: LouiseOffer;
  onAddToSelected: () => void;
  isComparing?: boolean;
  isOpen?: boolean;
  isSelected?: boolean;
}

const SingleOfferSummaryContainer: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
  onAddToSelected,
  isComparing,
  isOpen,
  isSelected,
}) => {
  const { t } = useTranslation();
  const isNewRoute = useIsNewRoute();
  const emailCalculationModal = useDisclosure();
  const { handleCloseOnline, isLoading } = useCloseOnline();

  // If it's not succesfull it's loading
  if (!isOfferSuccessfull(offer))
    return (
      <SummaryContainer>
        <VStack align="stretch" spacing="4" mx="4" mb="auto">
          <Skeleton h="4" w="50%" mt="8" />
          <Skeleton h="8" />
        </VStack>
        <Center bg="primary.500" h="14">
          <DotLoaderWithText textStyle="largeBody">
            {t('Calculating')}
          </DotLoaderWithText>
        </Center>
      </SummaryContainer>
    );

  const documents = [
    {
      label: t('TERMS'),
      url: offer.info?.terms || '',
    },
    {
      label: t('IPID'),
      url: offer.info?.ipid || '',
    },
  ];

  return (
    <SummaryContainer>
      <SingleOfferPrice
        interval={t(`spread.${offer.details.payment_interval.toUpperCase()}`)}
      >
        {Number(offer.premium.total_premium).toLocaleString('nl-BE', {
          style: 'currency',
          currency: offer.details.currency,
        })}
      </SingleOfferPrice>
      {(isOpen || isNewRoute) && !isSocialLoginUrl && (
        <SingleOfferDocuments title={t('Documents')} documents={documents} />
      )}
      {/* HIDDEN UNTIL WE HAVE THE REQUIRED INFORMATION FOR MAKING THIS USEFUL */}
      {/* FOR MORE INFO ASK MICHIEL */}
      {isNewRoute && (
        <VStack mb="4" spacing="4">
          <EmailCalculationModal
            isOpen={emailCalculationModal.isOpen}
            onClose={emailCalculationModal.onClose}
            quoteId={offer.id!}
            isExtendedInput={
              ['villasure'].includes(offer?.info?.company?.key) ||
              offer?.info?.insurance_type === 'boat'
            }
          />
          <OfferActionLink mb="0" onClick={emailCalculationModal.onOpen}>
            {t(
              'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_ACTIONS_SEND-CALCULATION',
            )}
          </OfferActionLink>
        </VStack>
      )}
      {isNewRoute ? (
        <SingleOfferMainButton
          onClick={() => handleCloseOnline(offer.id!)}
          dataTestId="ADVISORY-CONVO_OFFERS_SINGLE-OFFER_ADD-TO-OFFERS"
          isLoading={isLoading}
        >
          {t('SALES-CONVERSATION_OFFERS_SINGLE-OFFER_ACTIONS_CLOSE-ONLINE')}
        </SingleOfferMainButton>
      ) : (
        <SingleOfferMainButton
          onClick={onAddToSelected}
          dataTestId="ADVISORY-CONVO_OFFERS_SINGLE-OFFER_ADD-TO-OFFERS"
        >
          {isSelected
            ? t('Remove from selected offers')
            : t('Add to selected offers')}
        </SingleOfferMainButton>
      )}
    </SummaryContainer>
  );
};

export default SingleOfferSummaryContainer;
